import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {GeneralHttpHelperService} from '../../helpers/general-http-helper.service';
import {ForgotPasswordApiData, ForgotPasswordReqObj, LoginApiData, LoginReqObj} from './autentication-http-interfaces';
import {environment} from "../../../../environments/environment";

const BASE_V2_URL = `${environment.API_ENDPOINT_V2}`;

const AUTH_API_ROUTES = {
  LOGIN: 'login',
  LOGIN_WITH_GOOGLE: 'verify/google/token',
  LOGIN_WITH_APPLE: 'verify/apple/token',
  FORGOT_PASSWORD: 'password-reset',
  POST_REGISTER: 'register',
  POST_COMPANY_REGISTER: 'company/register',
  POST_VERIFY_ACCOUNT: 'verify-account',
  POST_RESEND_VERIFICATION_CODE: 'resend-code',
  GET_TOPICS: 'subcategories',
  POST_TOGGLE_TOPIC: 'subcategories/user/interested_in/:topicId',
  GET_USER_TOPICS: 'subcategories/user/interested_in',
  GET_LISTERS: 'suggested-listers',
  POST_TOGGLE_LISTER: 'interested-in-listers/:listerId',
  GET_USER_LISTERS: 'interested-in/listers',
  GET_LANGUAGES: 'languages',
  UPDATE_PHONE_NUMBER: 'users/update-phone-number',
  UPDATE_PHONE_NUMBER_SMS: 'verification-code/sms',
  APPLE_TOKEN: 'auth/apple/redirect-link',
  POST_LOGIN_WITH_CODE: 'login-with-code',
  FORGOT_PASSWORD_DEEP_LINK: 'password-reset-deep-link',
  POST_DEEP_LINK_RESET_PASSWORD: 'reset-password-by-temp',
};

@Injectable({
  providedIn: 'root'
})
export class AuthenticationHttpService {

  constructor(private httpClient: HttpClient,
              private generalHttpHelperService: GeneralHttpHelperService) {

  }

  private loginReq(bodyParams: LoginReqObj): Observable<LoginApiData> {
    return this.httpClient.post(AUTH_API_ROUTES.LOGIN, bodyParams) as any;
  }

  login(bodyParams: LoginReqObj): Promise<LoginApiData> {
    return new Promise<any>((resolve, reject) => {
      this.loginReq(bodyParams).subscribe((res: LoginApiData) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private forgotPasswordReq(bodyParams: ForgotPasswordReqObj): Observable<ForgotPasswordApiData> {
    return this.httpClient.post(AUTH_API_ROUTES.FORGOT_PASSWORD, bodyParams) as any;
  }

  forgotPassword(bodyParams: ForgotPasswordReqObj): Promise<ForgotPasswordApiData> {
    return new Promise<ForgotPasswordApiData>((resolve, reject) => {
      this.forgotPasswordReq(bodyParams).subscribe((res: ForgotPasswordApiData) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private postRegisterReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(AUTH_API_ROUTES.POST_REGISTER, bodyReq);
  }

  postRegister(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postRegisterReq(bodyReq).subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private postCompanyRegisterReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(AUTH_API_ROUTES.POST_COMPANY_REGISTER, bodyReq);
  }

  postCompanyRegister(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postCompanyRegisterReq(bodyReq).subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private postVerifyAccountReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(AUTH_API_ROUTES.POST_VERIFY_ACCOUNT, bodyReq);
  }

  postVerifyAccount(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postVerifyAccountReq(bodyReq).subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private postResendVerificationCodeReq(): Observable<any> {
    return this.httpClient.post(AUTH_API_ROUTES.POST_RESEND_VERIFICATION_CODE, {}) as any;
  }

  postResendVerificationCode(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.postResendVerificationCodeReq().subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private getTopicsReq(urlParams: any): Observable<any> {
    const url = this.generalHttpHelperService.addUrlParams(AUTH_API_ROUTES.GET_TOPICS, urlParams);
    return this.httpClient.get(url);
  }

  getTopics(urlParams: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getTopicsReq(urlParams).subscribe((res: any) => {
        resolve({
          data: res.data.data,
          pagination: this.generalHttpHelperService.returnPagination(res.data)
        });
      }, error => {
        reject(error);
      });
    });
  }

  private getUserTopicsReq(): Observable<any> {
    return this.httpClient.get(AUTH_API_ROUTES.GET_USER_TOPICS);
  }

  getUserTopics(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getUserTopicsReq().subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private getSuggestedListersReq(urlParams: any): Observable<any> {
    const url = this.generalHttpHelperService.addUrlParams(AUTH_API_ROUTES.GET_LISTERS, urlParams);
    return this.httpClient.get(url);
  }

  getSuggestedListers(urlParams: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getSuggestedListersReq(urlParams).subscribe((res: any) => {
        resolve({
          data: res.data,
          pagination: this.generalHttpHelperService.returnPagination(res)
        });
      }, error => {
        reject(error);
      });
    });
  }

  private getUserListersReq(): Observable<any> {
    return this.httpClient.get(AUTH_API_ROUTES.GET_USER_LISTERS);
  }

  getUserListers(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getUserListersReq().subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private bulkToggleFollowersV2Req(bodyParams: any): Observable<any> {
    return this.httpClient.post(`${BASE_V2_URL}users/followers/bulk-toggle`, bodyParams) as any;
  }

  bulkToggleFollowersV2(bodyParams: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bulkToggleFollowersV2Req(bodyParams).subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private bulkToggleSubcategoriesV2Req(bodyParams: any): Observable<any> {
    return this.httpClient.post(`${BASE_V2_URL}subcategories/bulkToggle`, bodyParams) as any;
  }

  bulkToggleSubcategoriesV2(bodyParams: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bulkToggleSubcategoriesV2Req(bodyParams).subscribe((res: any) => {
        resolve(res);
      }, error => {
        reject(error);
      });
    });
  }

  private postCreateNumberRegisterPostReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(
      BASE_V2_URL + AUTH_API_ROUTES.UPDATE_PHONE_NUMBER_SMS,
      bodyReq
    );
  }

  postCreateNumberRegisterPostCall(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postCreateNumberRegisterPostReq(bodyReq).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private postCreateNumberRegisterReq(bodyReq: any): Observable<any> {
    return this.httpClient.patch(
      BASE_V2_URL + AUTH_API_ROUTES.UPDATE_PHONE_NUMBER,
      bodyReq
    );
  }

  postCreateNumberRegister(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postCreateNumberRegisterReq(bodyReq).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private loginWithGoogleReq(bodyParams: any): Observable<any> {
    return this.httpClient.post(
      AUTH_API_ROUTES.LOGIN_WITH_GOOGLE,
      bodyParams
    ) as any;
  }

  loginWithGoogle(bodyParams: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.loginWithGoogleReq(bodyParams).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private loginWithAppleReq(bodyParams: any): Observable<any> {
    return this.httpClient.post(
      AUTH_API_ROUTES.LOGIN_WITH_APPLE,
      bodyParams
    ) as any;
  }

  loginWithApple(bodyParams: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.loginWithAppleReq(bodyParams).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private loginWithAppleGetRedirectReq(): Observable<any> {
    return this.httpClient.get(
      environment.BACKEND_ENDPOINT + AUTH_API_ROUTES.APPLE_TOKEN, {
        observe: 'response'
      }
    ) as any;
  }

  loginWithAppleGetRedirect(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.loginWithAppleGetRedirectReq().subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private postLoginWithCodeReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(
      BASE_V2_URL + AUTH_API_ROUTES.POST_LOGIN_WITH_CODE,
      bodyReq
    );
  }

  postLoginWithCode(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postLoginWithCodeReq(bodyReq).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private forgotPasswordDeepLinkReq(
    bodyParams: ForgotPasswordReqObj
  ): Observable<ForgotPasswordApiData> {
    return this.httpClient.post(
      AUTH_API_ROUTES.FORGOT_PASSWORD_DEEP_LINK,
      bodyParams
    ) as any;
  }

  forgotPasswordDeepLink(
    bodyParams: ForgotPasswordReqObj
  ): Promise<ForgotPasswordApiData> {
    return new Promise<ForgotPasswordApiData>((resolve, reject) => {
      this.forgotPasswordDeepLinkReq(bodyParams).subscribe(
        (res: ForgotPasswordApiData) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private resetPasswordDeepLinkReq(bodyReq: any): Observable<any> {
    return this.httpClient.post(
      BASE_V2_URL + AUTH_API_ROUTES.POST_DEEP_LINK_RESET_PASSWORD,
      bodyReq
    );
  }

  resetPasswordDeepLink(bodyReq: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resetPasswordDeepLinkReq(bodyReq).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
}
